import { useTranslation } from 'react-i18next'; // Hook for internationalization
import { useEffect, useMemo, useState } from 'react';
import { GetFirstSystemConfig, PartialUpdateSystemConfig } from '../../../../reduxToolkit/SystemConfig/SystemConfigApi';
import { useDispatch, useSelector } from 'react-redux';
import { getSettingConfigColumns, getSettingConfigRows, SETTING_CONFIG_MODEL } from '../Model/SettingConfigModel';
import HandleChange from '../../../../Helpers/HandleChange';
import { toast } from 'react-toastify';
import { permissionObj } from '../../../../Helpers/permission'; // Library for showing notifications
import { errorMsg } from '../../../../Apis/functions';
import { MIN_MAX } from '../../../../enums/MinMaxEnum';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 * SettingConfigApp
 *
 * A custom hook responsible for fetching, updating, and managing system configuration settings.
 * It integrates with Redux to dispatch actions and access global state.
 *
 * @returns {
 *   t: Function,
 *   DEV: boolean,
 *   permission: any,
 *   disabled: boolean,
 *   settingConfig: object,
 *   emptyFields: boolean,
 *   setSettingConfig: Function,
 *   handleUpdate: Function,
 *   handleChange: Function
 * }
 */
const SettingConfigApp = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(); // Initialize translation function
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Get development mode flag from Redux
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Get user permissions from Redux

    const [settingConfig, setSettingConfig] = useState({ ...SETTING_CONFIG_MODEL }); // Local state for the config form
    const [emptyFields, setEmptyFields] = useState(false); // Tracks if any fields are left empty
    const [disabled, setDisabled] = useState(false); // Tracks if any fields are left empty
    const { handleChange } = HandleChange({ model: settingConfig, setModel: setSettingConfig }); // Custom change handler for form fields
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isValidPeriodic, setIsValidPeriodic] = useState(true);
    const [isValidJustOnce, setIsValidJustOnce] = useState(true);
    const [isValidResend, setIsValidResend] = useState(true);

    /**
     * Marks a numeric field as valid while empty (the empty/required state is handled
     * on update via emptyFields) and invalid only when a typed value falls below the min.
     *
     * @param {string|number} rawValue - Current field value.
     * @param {number} min - Minimum accepted value.
     * @param {Function} setValid - Setter for the boolean validity state.
     */
    const validateMin = (rawValue, min, setValid) => {
        if (rawValue === '' || rawValue == null) { setValid(true); return; }
        const val = Number(rawValue);
        setValid(!isNaN(val) && val >= min);
    };

    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) {
            setExpandedRowId(row?.id);
        }
    };

    // Fetch the initial system configuration when the component mounts
    useEffect(() => {
        setDisabled(!DEV && !permission.includes(permissionObj.WEB_SYSTEM_CONFIG_UPDATE));
    }, [permission]);

    useEffect(() => {
        dispatch(GetFirstSystemConfig()).then(
            ({
                payload: {
                    data: { data, success },
                },
            }) => {
                if (success) {
                    setSettingConfig(SETTING_CONFIG_MODEL.set(data)); // Populate form with fetched config
                }
            },
        );
    }, []);

    /** Re-validate each numeric field whenever its value changes. */
    useEffect(() => {
        validateMin(settingConfig.accessTolerancePeriodicMin, MIN_MAX.ACCESS_TOLERANCE.start, setIsValidPeriodic);
    }, [settingConfig.accessTolerancePeriodicMin]);

    useEffect(() => {
        validateMin(settingConfig.accessToleranceJustOnceMin, MIN_MAX.ACCESS_TOLERANCE.start, setIsValidJustOnce);
    }, [settingConfig.accessToleranceJustOnceMin]);

    useEffect(() => {
        validateMin(settingConfig.resendInvitationHour, MIN_MAX.RESEND_INVITATION_HOUR.start, setIsValidResend);
    }, [settingConfig.resendInvitationHour]);

    // Handles updating the system configuration
    const handleUpdate = () => {
        if (!settingConfig.isValid()) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        setEmptyFields(false);

        dispatch(PartialUpdateSystemConfig(settingConfig.toRegister())).then(
            ({
                payload: {
                    data: { success, code },
                },
            }) => {
                if (success === true) {
                    toast.success(t('has_been_updated_successfully'));
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    const columns = useMemo(() => getSettingConfigColumns({t}), [t]);

    const rows = useMemo(
        () =>
            getSettingConfigRows({
                t,
                settingConfig,
                handleChange,
                disabled,
                emptyFields,
                isValidPeriodic,
                isValidJustOnce,
                isValidResend,
            }),
        [t, settingConfig, handleChange, disabled, emptyFields, isValidPeriodic, isValidJustOnce, isValidResend],
    );

    // Return state and handlers to be used in the component
    return {
        t,
        DEV,
        permission,
        disabled,
        settingConfig,
        emptyFields,
        setSettingConfig,
        handleUpdate,
        handleChange,
        handleRowExpand,
        columns,
        rows,
    };
};

export default SettingConfigApp;
